import type { ApolloError, FetchResult } from '@apollo/client';
import { gql, useMutation } from '@apollo/client';
import { Str } from '@livecontrol/core-utils';
import type { Asset } from '@livecontrol/scheduler/model';
import { useCallback, useState } from 'react';
import { Errors } from '../graphql';
import type { MutationResult } from '../graphql';

interface TVariables {
  assetId: number;
  title: string;
  description: string;
  hidden: boolean;
  startTime: number;
  endTime: number;
  clientId: number;
}

interface TData {
  createClip: {
    error: boolean;
    clip: Asset.Clip;
  };
}

interface Args {
  assetId: number;
  title: string;
  description: string;
  hidden: boolean;
  startTime: number;
  endTime: number;
  clientId: number;
  type?: string;
}

const MUTATION = gql`
  mutation CreateClip(
    $assetId: Int!
    $title: String
    $description: String
    $startTime: Float!
    $endTime: Float!
    $hidden: Boolean
    $clientId: Int!
    $type: String
  ) {
    createClip(
      assetId: $assetId
      title: $title
      description: $description
      startTime: $startTime
      endTime: $endTime
      hidden: $hidden
      clientId: $clientId
      type: $type
    ) {
      error
      clip {
        id
        assetId
        title
        description
        start_time
        end_time
        status
        type
        hidden
        url
        created_at
        updated_at
      }
    }
  }
`;

export const useCreateClip = (): [
  (args: Args) => Promise<Asset.Clip | undefined>,
  MutationResult<Asset.Clip, 'clip'>
] => {
  const [mutation, result] = useMutation<TData, TVariables>(MUTATION);

  const [error, setError] = useState<Error | undefined>();
  const [clip, setClip] = useState<Asset.Clip | undefined>();

  return [
    useCallback(
      async (args: Args): Promise<Asset.Clip | undefined> => {
        let clip_: Asset.Clip | undefined;

        try {
          const variables = {
            title: Str.normalize(args.title) ?? '',
            description: Str.normalize(args.description) ?? '',
            startTime: args.startTime,
            endTime: args.endTime,
            hidden: args.hidden,
            clientId: args.clientId,
            assetId: args.assetId,
            type: args.type ? args.type : 'clip'
          };

          // Execute the GraphQL mutation
          const response = await mutation({ variables })
            .then(({ data }: FetchResult<TData>) => {
              if (data?.createClip.error) {
                throw new Error('Error creating clip');
              }

              return data?.createClip.clip;
            })
            .catch((_error: ApolloError) => {
              throw Errors.serverError();
            });

          clip_ = response;

          if (!clip_) {
            throw Errors.serverError();
          }
        } catch (error_: unknown) {
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          setError(error_ as Error);
        }

        setClip(clip_);

        return clip_;
      },
      [mutation, setError, setClip]
    ),
    {
      clip,
      error,
      called: result.called,
      loading: result.loading
    }
  ];
};
